import { Link } from 'react-router';
import { ExternalLink, MapPin, MonitorSmartphone } from 'lucide-react';
import { calendarEventLabel } from '@fas/attendance-rules';
import { DAYS_OF_WEEK, type ScheduleDto, type ScheduleOccurrenceDto } from '@fas/types';
import { Badge, cn } from '@fas/ui';
import { formatDate, labelize } from '@/lib/format';
import { dayName, MODALITY_LABELS, timeRange } from './labels';

const EXCEPTION_NOTE = {
  CANCELLED: 'Cancelled',
  RESCHEDULED: 'Moved',
  ROOM_CHANGE: 'Room changed',
  MODALITY_CHANGE: 'Modality changed',
} as const;

/** One class meeting on a specific date (after date-specific changes are applied). */
export function OccurrenceCard({ o, compact = false }: { o: ScheduleOccurrenceDto; compact?: boolean }) {
  return (
    <article
      className={cn(
        'rounded-md border bg-white p-3 text-sm shadow-sm',
        o.cancelled ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-blue-200',
      )}
      aria-label={`${o.courseCode} ${o.sectionCode}, ${timeRange(o.startTime, o.endTime)}${o.cancelled ? ', cancelled' : ''}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn('font-semibold', o.cancelled && 'line-through')}>{timeRange(o.startTime, o.endTime)}</p>
        {o.exceptionType ? <Badge tone={o.cancelled ? 'neutral' : 'warning'}>{EXCEPTION_NOTE[o.exceptionType]}</Badge> : null}
      </div>
      <p className={cn('mt-1 font-medium', o.cancelled && 'line-through')}>
        {o.courseCode} {o.sectionCode}
      </p>
      {!compact ? <p className="text-xs text-slate-600">{o.courseTitle}</p> : null}
      <p className="mt-1 flex items-center gap-1 text-xs text-slate-600">
        {o.modality === 'FACE_TO_FACE' || o.modality === 'FIELD_CLINICAL' ? <MapPin className="size-3.5" aria-hidden /> : <MonitorSmartphone className="size-3.5" aria-hidden />}
        {o.locationName ?? MODALITY_LABELS[o.modality]}
        {o.locationName ? ` · ${MODALITY_LABELS[o.modality]}` : ''}
      </p>
      {o.meetingUrl && !o.cancelled ? (
        <a href={o.meetingUrl} target="_blank" rel="noreferrer noopener" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline">
          Join online class <ExternalLink className="size-3" aria-hidden />
        </a>
      ) : null}
      {o.calendarEvent ? (
        <p className="mt-1 text-xs font-medium text-slate-700">
          {calendarEventLabel(o.calendarEvent.eventType)}: {o.calendarEvent.title}
        </p>
      ) : null}
      {o.exceptionReason ? <p className="mt-1 text-xs italic text-slate-600">{o.exceptionReason}</p> : null}
      {o.loadType !== 'REGULAR' ? <p className="mt-1 text-xs text-slate-500">{labelize(o.loadType)}</p> : null}
    </article>
  );
}

/** Week grid of dated occurrences (Monday first); stacks into a list on small screens. */
export function WeekTimetable({ dates, occurrences, today }: { dates: string[]; occurrences: ScheduleOccurrenceDto[]; today: string }) {
  return (
    <div className="grid gap-3 md:grid-cols-7">
      {dates.map((date, i) => {
        const items = occurrences.filter((o) => o.date === date);
        const isToday = date === today;
        return (
          <section key={date} aria-labelledby={`day-${date}`} className={cn('rounded-lg border p-2', isToday ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200 bg-white')}>
            <h3 id={`day-${date}`} className="mb-2 text-sm font-semibold">
              {dayName(DAYS_OF_WEEK[i]!)} <span className="font-normal text-slate-600">{formatDate(date)}</span>
              {isToday ? <span className="ml-1 text-xs font-medium text-blue-800">(today)</span> : null}
            </h3>
            {items.length === 0 ? (
              <p className="text-xs text-slate-500">No classes</p>
            ) : (
              <ul className="space-y-2">
                {items.map((o) => (
                  <li key={`${o.scheduleId}-${o.date}`}>
                    <OccurrenceCard o={o} compact />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Recurring weekly pattern (no dates) — used on the faculty profile. */
export function WeeklyPattern({ schedules, linkSchedules = true }: { schedules: ScheduleDto[]; linkSchedules?: boolean }) {
  const days = DAYS_OF_WEEK.filter((d) => schedules.some((s) => s.dayOfWeek === d));
  if (days.length === 0) return <p className="text-sm text-slate-600">No schedules in this term.</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {days.map((d) => (
        <section key={d} aria-labelledby={`pattern-${d}`} className="rounded-lg border border-slate-200 p-3">
          <h3 id={`pattern-${d}`} className="mb-2 text-sm font-semibold">
            {dayName(d)}
          </h3>
          <ul className="space-y-2 text-sm">
            {schedules
              .filter((s) => s.dayOfWeek === d)
              .sort((a, b) => a.startTime.localeCompare(b.startTime))
              .map((s) => (
                <li key={s.id}>
                  {linkSchedules ? (
                    <Link to={`/schedules/${s.id}`} className="font-medium text-blue-700 hover:underline">
                      {timeRange(s.startTime, s.endTime)} · {s.courseCode} {s.sectionCode}
                    </Link>
                  ) : (
                    <span className="font-medium">
                      {timeRange(s.startTime, s.endTime)} · {s.courseCode} {s.sectionCode}
                    </span>
                  )}
                  <p className="text-xs text-slate-600">
                    {s.locationName ?? MODALITY_LABELS[s.scheduledModality]}
                    {s.status !== 'ACTIVE' ? ` · ${labelize(s.status)}` : ''}
                  </p>
                </li>
              ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
